
import { useEffect, useState, useRef } from 'react';
import { categories } from '../../../mocks/products';

interface Product {
  id: number;
  name: string;
  category: string;
  subcategory: string;
  price: number;
  unit: string;
  dimensions: string;
  material?: string;
  pricePerPiece: number;
  pricePerUnit: number;
  pricePerSqm: number | null;
  image: string;
  images?: string[];
  description?: string;
}

interface ProductModalProps {
  product: Product | null;
  onClose: () => void;
}

export default function ProductModal({ product, onClose }: ProductModalProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [showQuotationSlide, setShowQuotationSlide] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragDelta, setDragDelta] = useState(0);
  // Mobile fullscreen image viewer state
  const [mobileFullscreen, setMobileFullscreen] = useState(false);
  const [fsIsDragging, setFsIsDragging] = useState(false);
  const [fsDragStartX, setFsDragStartX] = useState(0);
  const [fsDragDelta, setFsDragDelta] = useState(0);

  const dragThreshold = 40;
  const imageContainerRef = useRef<HTMLDivElement>(null);

  const imageList = product?.images && product.images.length > 1 ? product.images : (product ? [product.image] : []);
  const hasMultipleImages = imageList.length > 1;

  useEffect(() => {
    if (product) {
      document.body.style.overflow = 'hidden';
      setCurrentImageIndex(0);
      requestAnimationFrame(() => {
        setIsVisible(true);
      });
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [product]);

  const handleClose = () => {
    setIsVisible(false);
    setShowQuotationSlide(false);
    setCurrentImageIndex(0);
    setMobileFullscreen(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const goToPrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + imageList.length) % imageList.length);
  };

  const goToNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % imageList.length);
  };

  const goToIndex = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    setCurrentImageIndex(index);
  };

  // Touch / mouse drag support (modal image)
  const handlePointerDown = (e: React.PointerEvent) => {
    if (!hasMultipleImages) return;
    setIsDragging(true);
    setDragStartX(e.clientX);
    setDragDelta(0);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    setDragDelta(e.clientX - dragStartX);
  };

  const handlePointerUp = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (dragDelta < -dragThreshold) {
      setCurrentImageIndex((prev) => (prev + 1) % imageList.length);
    } else if (dragDelta > dragThreshold) {
      setCurrentImageIndex((prev) => (prev - 1 + imageList.length) % imageList.length);
    }
    setDragDelta(0);
  };

  // Fullscreen drag support
  const handleFsPointerDown = (e: React.PointerEvent) => {
    if (!hasMultipleImages) return;
    setFsIsDragging(true);
    setFsDragStartX(e.clientX);
    setFsDragDelta(0);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handleFsPointerMove = (e: React.PointerEvent) => {
    if (!fsIsDragging) return;
    e.preventDefault();
    setFsDragDelta(e.clientX - fsDragStartX);
  };

  const handleFsPointerUp = () => {
    if (!fsIsDragging) return;
    setFsIsDragging(false);
    if (fsDragDelta < -dragThreshold) {
      setCurrentImageIndex((prev) => (prev + 1) % imageList.length);
    } else if (fsDragDelta > dragThreshold) {
      setCurrentImageIndex((prev) => (prev - 1 + imageList.length) % imageList.length);
    }
    setFsDragDelta(0);
  };

  const handleSendQuotation = () => {
    if (!product) return;
    const categoryName = categories.find((c) => c.id === product.category)?.name || '';
    const subject = encodeURIComponent(`Quotation Request: ${product.name}`);
    const body = encodeURIComponent(
      `Hello South Dev Home Depot,\n\nI would like to request a quotation for the following product:\n\nProduct: ${product.name}\nCategory: ${categoryName}\nSubcategory: ${product.subcategory}\nDimensions: ${product.dimensions}\nListed Price: $${product.price.toFixed(2)} ${product.unit}\n\nPlease provide me with a detailed quotation including bulk pricing and availability.\n\nThank you.`
    );

    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (isMobile) {
      window.location.href = `googlegmail://co?to=southdevhomedepo2020@gmail.com&subject=${subject}&body=${body}`;
    } else {
      window.open(
        `https://mail.google.com/mail/?view=cm&fs=1&to=southdevhomedepo2020@gmail.com&su=${subject}&body=${body}`,
        '_blank'
      );
    }
  };

  if (!product) return null;

  const categoryName = categories.find((c) => c.id === product.category)?.name || '';

  return (
    <>
      {/* ── Mobile Fullscreen Image Viewer (lg: hidden) ── */}
      {mobileFullscreen && (
        <div
          className="fixed inset-0 z-[200] bg-black flex flex-col lg:hidden"
          style={{ touchAction: 'none' }}
        >
          {/* Top bar */}
          <div className="flex items-center justify-between px-4 py-3 bg-black/80 backdrop-blur-sm shrink-0">
            <button
              onClick={() => setMobileFullscreen(false)}
              className="flex items-center gap-1.5 text-white/80 active:text-white cursor-pointer"
            >
              <i className="ri-arrow-left-line text-lg"></i>
              <span className="text-sm font-medium">Back</span>
            </button>
            <span className="text-white/60 text-xs font-medium truncate max-w-[55%] text-center">{product.name}</span>
            <button
              onClick={handleClose}
              className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-full text-white cursor-pointer"
            >
              <i className="ri-close-line text-base"></i>
            </button>
          </div>

          {/* Fullscreen image area */}
          <div
            className="flex-1 relative overflow-hidden select-none"
            onPointerDown={handleFsPointerDown}
            onPointerMove={handleFsPointerMove}
            onPointerUp={handleFsPointerUp}
            onPointerLeave={handleFsPointerUp}
          >
            {imageList.map((src, index) => (
              <img
                key={index}
                src={src}
                alt={`${product.name} ${index + 1}`}
                draggable={false}
                className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-400 ${
                  index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                }`}
                style={{
                  transform: index === currentImageIndex && fsIsDragging
                    ? `translateX(${fsDragDelta * 0.3}px)`
                    : undefined,
                  transition: fsIsDragging ? 'none' : undefined,
                }}
              />
            ))}

            {/* Subcategory badge */}
            <div className="absolute top-3 left-3 bg-[#FF6600] text-white text-[10px] font-semibold px-2.5 py-1 rounded-full z-10">
              {product.subcategory}
            </div>

            {/* Arrows */}
            {hasMultipleImages && (
              <>
                <button
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={goToPrev}
                  className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 flex items-center justify-center bg-black/50 rounded-full text-white cursor-pointer backdrop-blur-sm"
                >
                  <i className="ri-arrow-left-s-line text-xl"></i>
                </button>
                <button
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={goToNext}
                  className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-9 h-9 flex items-center justify-center bg-black/50 rounded-full text-white cursor-pointer backdrop-blur-sm"
                >
                  <i className="ri-arrow-right-s-line text-xl"></i>
                </button>
              </>
            )}

            {/* Swipe hint */}
            {hasMultipleImages && (
              <div className="absolute bottom-16 left-0 right-0 flex items-center justify-center pointer-events-none">
                <div className="flex items-center gap-1 bg-black/40 backdrop-blur-sm rounded-full px-3 py-1">
                  <i className="ri-arrow-left-s-line text-white/60 text-xs"></i>
                  <span className="text-white/60 text-[10px]">Swipe to browse</span>
                  <i className="ri-arrow-right-s-line text-white/60 text-xs"></i>
                </div>
              </div>
            )}

            {/* Dot indicators */}
            {hasMultipleImages && (
              <div className="absolute bottom-5 left-0 right-0 flex items-center justify-center gap-1.5 z-20">
                {imageList.map((_, index) => (
                  <button
                    key={index}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => goToIndex(e, index)}
                    className={`rounded-full transition-all duration-300 cursor-pointer ${
                      index === currentImageIndex
                        ? 'w-4 h-1.5 bg-white'
                        : 'w-1.5 h-1.5 bg-white/40'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Bottom action bar */}
          <div className="shrink-0 px-4 py-3 bg-black/80 backdrop-blur-sm">
            <button
              onClick={handleSendQuotation}
              className="w-full bg-[#FF6600] hover:bg-[#E55A00] text-white font-bold py-3 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap active:scale-[0.98] text-sm"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-mail-send-line text-base"></i>
              </div>
              Send Quotation
            </button>
          </div>
        </div>
      )}

      {/* ── Main Modal ── */}
      <div
        className={`fixed inset-0 z-[100] flex items-center justify-center transition-all duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={handleClose}
      >
        {/* Blurred backdrop */}
        <div
          className={`absolute inset-0 bg-black/50 backdrop-blur-md transition-all duration-300 ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
        />

        {/* Modal content */}
        <div
          className={`relative z-10 bg-[#1A1A1A] rounded-none lg:rounded-2xl overflow-hidden border-0 lg:border border-white/10 shadow-2xl shadow-black/50 w-full lg:max-w-3xl lg:mx-4 h-full lg:h-auto lg:max-h-[85vh] overflow-y-auto transition-all duration-300 ${
            isVisible ? 'scale-100 translate-y-0' : 'scale-90 translate-y-8'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-2.5 right-2.5 lg:top-3 lg:right-3 z-20 w-8 h-8 flex items-center justify-center bg-black/50 hover:bg-[#FF6600] rounded-full text-white transition-all duration-200 cursor-pointer"
          >
            <i className="ri-close-line text-base lg:text-lg"></i>
          </button>

          <div className="flex flex-col lg:flex-row lg:items-stretch">
            {/* Left - Product Image Carousel */}
            <div
              ref={imageContainerRef}
              className="lg:w-1/2 relative bg-[#141414] overflow-hidden self-stretch select-none"
              style={{ touchAction: 'none' }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
            >
              {/* Spacer for mobile */}
              <div className="aspect-[4/3] lg:hidden w-full" />

              {/* Images */}
              {imageList.map((src, index) => (
                <img
                  key={index}
                  src={src}
                  alt={`${product.name} ${index + 1}`}
                  draggable={false}
                  className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ${
                    index === currentImageIndex
                      ? showQuotationSlide ? 'scale-110 opacity-100' : 'scale-100 opacity-100'
                      : 'scale-100 opacity-0'
                  }`}
                  style={{
                    transform: index === currentImageIndex && isDragging
                      ? `translateX(${dragDelta * 0.3}px) ${showQuotationSlide ? 'scale(1.1)' : 'scale(1)'}`
                      : undefined,
                    transition: isDragging ? 'none' : undefined,
                  }}
                />
              ))}

              {/* Mobile tap-to-fullscreen overlay (lg: hidden) */}
              <button
                className="absolute inset-0 z-10 lg:hidden flex items-end justify-end p-2.5 cursor-pointer bg-transparent"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); setMobileFullscreen(true); }}
                aria-label="View fullscreen"
              >
                <div className="flex items-center gap-1 bg-black/50 backdrop-blur-sm rounded-full px-2.5 py-1 pointer-events-none">
                  <i className="ri-fullscreen-line text-white text-xs"></i>
                  <span className="text-white text-[10px] font-medium">Tap to expand</span>
                </div>
              </button>

              {/* Subcategory badge */}
              <div className="absolute top-2.5 left-2.5 lg:top-3 lg:left-3 bg-[#FF6600] text-white text-[10px] font-semibold px-2 py-0.5 lg:px-2.5 lg:py-1 rounded-full z-10">
                {product.subcategory}
              </div>

              {/* Overlay gradient when zoomed */}
              <div className={`absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent transition-opacity duration-500 pointer-events-none ${showQuotationSlide ? 'opacity-100' : 'opacity-0'}`} />

              {/* Swipe hint for mobile */}
              {hasMultipleImages && (
                <div className="absolute bottom-8 left-0 right-0 flex items-center justify-center z-10 pointer-events-none lg:hidden">
                  <div className="flex items-center gap-1 bg-black/40 backdrop-blur-sm rounded-full px-2.5 py-1">
                    <i className="ri-arrow-left-s-line text-white/60 text-xs"></i>
                    <span className="text-white/60 text-[10px]">Swipe to browse</span>
                    <i className="ri-arrow-right-s-line text-white/60 text-xs"></i>
                  </div>
                </div>
              )}

              {/* Left Arrow */}
              {hasMultipleImages && (
                <button
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={goToPrev}
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-7 h-7 lg:w-8 lg:h-8 flex items-center justify-center bg-black/50 hover:bg-black/80 rounded-full text-white transition-all duration-200 cursor-pointer backdrop-blur-sm"
                >
                  <i className="ri-arrow-left-s-line text-base lg:text-lg"></i>
                </button>
              )}

              {/* Right Arrow */}
              {hasMultipleImages && (
                <button
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={goToNext}
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-7 h-7 lg:w-8 lg:h-8 flex items-center justify-center bg-black/50 hover:bg-black/80 rounded-full text-white transition-all duration-200 cursor-pointer backdrop-blur-sm"
                >
                  <i className="ri-arrow-right-s-line text-base lg:text-lg"></i>
                </button>
              )}

              {/* Dot indicators */}
              {hasMultipleImages && (
                <div className="absolute bottom-2.5 left-0 right-0 flex items-center justify-center gap-1.5 z-20">
                  {imageList.map((_, index) => (
                    <button
                      key={index}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => goToIndex(e, index)}
                      className={`rounded-full transition-all duration-300 cursor-pointer ${
                        index === currentImageIndex
                          ? 'w-4 h-1.5 bg-white'
                          : 'w-1.5 h-1.5 bg-white/40 hover:bg-white/70'
                      }`}
                    />
                  ))}
                </div>
              )}

              {/* Product name overlay when zoomed */}
              {showQuotationSlide && (
                <div className="absolute bottom-2 left-2 right-2 lg:bottom-3 lg:left-3 lg:right-3 z-20">
                  <div className="bg-black/20 backdrop-blur-sm rounded-lg px-3 py-1.5 lg:px-4 lg:py-2">
                    <p className="text-white/50 text-[10px] uppercase tracking-widest mb-0.5">{categoryName}</p>
                    <p className="text-white/80 font-semibold text-xs lg:text-sm leading-tight">{product.name}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Right - Specifications */}
            <div className="lg:w-1/2 p-4 lg:p-6 flex flex-col">

              {/* Category & Name */}
              <div className="mb-3 lg:mb-4">
                <p className="text-white/40 text-[10px] uppercase tracking-widest mb-0.5">{categoryName}</p>
                <h2 className="text-white font-bold text-base lg:text-xl leading-snug">{product.name}</h2>
              </div>

              {/* Dimensions */}
              <div className="mb-3 lg:mb-4">
                <div className="flex items-center gap-1.5 mb-1.5 lg:mb-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-ruler-line text-[#FF6600] text-sm"></i>
                  </div>
                  <span className="text-white/60 text-[10px] lg:text-xs font-medium uppercase tracking-wider">
                    Dimensions
                  </span>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 lg:px-4 lg:py-2.5">
                  {product.dimensions.includes('|') ? (
                    <div className="flex flex-col gap-0.5">
                      {product.dimensions.split('|').map((dim, i) => (
                        <span key={i} className="text-white text-xs lg:text-sm font-semibold">{dim.trim()}</span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-white text-xs lg:text-sm font-semibold">{product.dimensions}</span>
                  )}
                </div>
              </div>

              {/* Material */}
              {product.material && (
                <div className="mb-3 lg:mb-4">
                  <div className="flex items-center gap-1.5 mb-1.5 lg:mb-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-stack-line text-[#FF6600] text-sm"></i>
                    </div>
                    <span className="text-white/60 text-[10px] lg:text-xs font-medium uppercase tracking-wider">
                      Material
                    </span>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 lg:px-4 lg:py-2.5">
                    <span className="text-white text-xs lg:text-sm font-semibold">{product.material}</span>
                  </div>
                </div>
              )}

              {/* Pricing breakdown */}
              <div className="mb-3 lg:mb-4">
                <div className="flex items-center gap-1.5 mb-1.5 lg:mb-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-price-tag-3-line text-[#FF6600] text-sm"></i>
                  </div>
                  <span className="text-white/60 text-[10px] lg:text-xs font-medium uppercase tracking-wider">
                    Pricing
                  </span>
                </div>
                <div className="flex items-center justify-center bg-white/5 border border-[#FF6600]/20 rounded-lg px-3 py-3 lg:px-4 lg:py-3.5">
                  <span className="text-[#FF6600] font-bold text-sm lg:text-base italic tracking-widest uppercase">
                    Prices Coming Soon
                  </span>
                </div>
              </div>

              {/* Description */}
              {product.description && (
                <div className="mb-3 lg:mb-4">
                  <div className="flex items-center gap-1.5 mb-1.5 lg:mb-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-file-text-line text-[#FF6600] text-sm"></i>
                    </div>
                    <span className="text-white/60 text-[10px] lg:text-xs font-medium uppercase tracking-wider">
                      Description
                    </span>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 lg:px-4 lg:py-3">
                    <p className="text-white/80 text-xs lg:text-sm leading-relaxed">{product.description}</p>
                  </div>
                </div>
              )}

              {/* Send Quotation Button */}
              <div className="mt-auto pt-2">
                <button
                  onClick={handleSendQuotation}
                  className="w-full bg-[#FF6600] hover:bg-[#E55A00] text-white font-bold py-2.5 lg:py-3 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap hover:scale-[1.02] active:scale-[0.98] text-sm"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-mail-send-line text-base"></i>
                  </div>
                  Send Quotation
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
